<template>
  <div class="server-settings">
    <settings-layout
      v-model="configs"
      :items="configs"
      :isAdmin="isAdmin"
      :loading="serversLoading"
      :properties="properties"
      :status="status"
      :testLoading="testLoading"
      @click:save="save"
      @click:test="test"
    >
      <template #type>
        {{ $t('server.configuration') }}
      </template>
      <template #actions v-if="configs.server_id">
        <b-dropdown-item @click="openDialogRemove" aria-role="listitem">{{ $t('lists.deleteDetail') }}</b-dropdown-item>
      </template>

      <template #column-right>
        <status-indicator :status="configs.status" class="status-indicator"></status-indicator>
        <div class="buttons">
          <button
            :disabled="configs.status === 'processing' || configs.status !== 'STANDBY' || configs.status === 'OFFLINE'"
            @click="runServers(configs.server_id)"
            class="button is-success play"
          >
            <span class="icon is-small">
              <i class="fas fa-play"></i>
            </span>
          </button>
          <button
            :disabled="configs.status === 'processing' || configs.status === 'STANDBY' || configs.status === 'OFFLINE'"
            @click="openDialogStop(configs.server_id)"
            class="button is-danger stop"
          >
            <span class="icon is-small">
              <i class="fas fa-stop"></i>
            </span>
          </button>
          <button
            :disabled="configs.status === 'processing' || configs.status === 'OFFLINE'"
            @click="openDialogRestart(configs.server_id)"
            class="button reload"
          >
            <span class="icon is-small">
              <i class="fas fa-redo"></i>
            </span>
          </button>
        </div>
        <a v-if="logs" class="logs-link" :href="logs" target="_blank"
          >{{ $t('server.logs') }}<i class="fas fa-external-link-alt"></i
        ></a>
      </template>
    </settings-layout>
    <hr class="hr-assets" />
    <div v-if="assets_numbers" class="assets_numbers">
      <router-link :to="{ name: 'models', params: configs.server_id }" class="button models">
        <span
          ><b>{{ $t('server.models.title') }}</b
          >: {{ assets_numbers.models }}</span
        >
      </router-link>
      <router-link :to="{ name: 'sources', params: configs.server_id }" class="button sources">
        <span
          ><b>{{ $t('server.sources.title') }}</b
          >: {{ assets_numbers.sources }}</span
        >
      </router-link>
      <router-link :to="{ name: 'pipelines', params: configs.server_id }" class="button pipelines">
        <span
          ><b>{{ $t('server.pipelines.title') }}</b
          >: {{ assets_numbers.pipelines }}</span
        >
      </router-link>
      <router-link :to="{ name: 'outputs', params: configs.server_id }" class="button outputs">
        <span
          ><b>{{ $t('server.outputs.title') }}</b
          >: {{ assets_numbers.outputs }}</span
        >
      </router-link>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers, mapActions } from 'vuex'
import SettingsLayout from '@/layout/SettingsLayout'
import StatusIndicator from '@/components/StatusIndicator'

const { mapState: mapUserState } = createNamespacedHelpers('general')

export default {
  name: 'Source',
  components: {
    SettingsLayout,
    StatusIndicator
  },
  computed: {
    ...mapUserState({
      role: state => state.user.role,
      title: state => state.title
    }),
    isAdmin() {
      return this.role === 'ADMIN'
    }
  },
  data() {
    return {
      serversLoading: false,
      testLoading: false,
      configs: {},
      properties: {},
      assets_numbers: {},
      status: '',
      logs: ''
    }
  },
  created() {
    if (!this.isAdmin) {
      this.$router.push({
        name: 'servers'
      })
    }
    this.getDefaultConfig()
  },
  methods: {
    ...mapActions('general', ['setTitle']),
    refetchStatus() {
      const body = {
        server_id: this.configs.server_id
      }
      const searchParams = new URLSearchParams(body)
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/server/status?${searchParams}`
      fetch(url, {
        method: 'GET',
        credentials: 'include'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(data => {
          this.configs.status = data.status
          this.configs.inputs_models = data.inputs_models
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )

      if (this.$route.name == 'server') {
        setTimeout(this.refetchStatus, 2000)
      }
    },
    runServers(server_id) {
      let server_ids = [server_id]
      this.configs.status = 'processing'
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/servers/start`
      fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_ids: server_ids
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.notifyServer({
            name: this.configs.name,
            status: 'running'
          })
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
    },
    openDialogStop(server_id) {
      let server_ids = [server_id]
      this.$dialog.confirm({
        message: `${this.$t('confirm.stop', {
          msg: this.configs.name
        })}?`,
        onConfirm: () => {
          this.configs.status = 'processing'
          this.stopServers(server_ids)
            .then(res => {
              if (!res.ok) {
                throw res.json()
              }
              return res.json()
            })
            .then(() => {
              this.serversLoading = false
              this.notifyServer({
                name: this.configs.name,
                status: 'stopped'
              })
            })
            .catch(error =>
              error.then(data => {
                this.$notify({
                  message: JSON.parse(data.message).message,
                  type: 'danger'
                })
              })
            )
            .finally(() => {
              this.serversLoading = false
            })
        }
      })
    },
    stopServers(server_ids) {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/servers/stop`
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_ids: server_ids
        }),
        method: 'POST'
      })
    },
    openDialogRestart(server_id) {
      let server_ids = [server_id]
      this.$dialog.confirm({
        message: `${this.$t('confirm.restart', {
          msg: this.configs.name
        })}?`,
        onConfirm: () => {
          this.configs.status = 'processing'
          this.restartServers(server_ids)
            .then(res => {
              if (!res.ok) {
                throw res.json()
              }
              return res.json()
            })
            .then(() => {
              this.notifyServer({
                name: this.configs.name,
                status: 'running'
              })
            })
            .catch(error =>
              error.then(data => {
                this.$notify({
                  message: JSON.parse(data.message).message,
                  type: 'danger'
                })
              })
            )
            .finally(() => {
              this.serversLoading = false
            })
        }
      })
    },
    restartServers(server_ids) {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/servers/restart`
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_ids: server_ids
        }),
        method: 'POST'
      })
    },
    openDialogRemove() {
      let server_ids = [this.configs.server_id]
      this.$dialog.confirm({
        message: `${this.$t('confirm.remove', {
          msg: this.configs.name
        })}?`,
        onConfirm: () => {
          this.configs.status = 'processing'
          this.removeServers(server_ids).finally(() => {
            this.serversLoading = false
            this.notifyServer({
              name: this.configs.name,
              status: 'deleted'
            })
          })
        }
      })
    },
    removeServers(server_ids) {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/servers/delete`
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_ids: server_ids
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.$router.push({
            name: 'servers'
          })
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
    },
    notifyServer(variables) {
      this.$notify({
        message: `${this.$t('notification.serverWas', {
          server: variables.name,
          status: this.$t(`notification.server.${variables.status}`)
        })}.`,
        type: 'success'
      })
    },
    test() {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/server/test`
      this.testLoading = true
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_config: this.configs
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(status => {
          this.status = status
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.testLoading = false
        })
    },
    getDefaultConfig() {
      if (!this.serversLoading) {
        if (this.$route.name === 'add_server' && this.$route.query.duplicate != undefined) {
          return this.duplicate()
        }

        const body = {
          server_id: this.$route.params.server_id
        }

        const searchParams = new URLSearchParams(body)
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/server?${searchParams}`
        this.serversLoading = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            this.configs = data.config
            this.properties = data.properties
            this.logs = data.logs
            this.assets_numbers = data.assets_numbers
            this.setTitle(this.configs.name)
            if (this.$route.params.server_id !== undefined) {
              this.refetchStatus()
            }
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.serversLoading = false
          })
      }
    },
    save() {
      const url = (process.env.VUE_APP_API_ENDPOINT || window.location.origin) + '/api/server'
      this.configs.name = this.title
      fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_config: this.configs
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.$router.push({
            name: 'servers'
          })
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
      //        .finally(() => {
      //          this.$router.go()
      //        })
    },
    toDuplicate() {
      this.$router.push({
        name: 'add_server',
        query: {
          duplicate: this.$route.params.server_id
        }
      })
    },
    duplicate() {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/server/duplicate`
      this.serversLoading = true
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          server_id: this.$route.query.duplicate
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(data => {
          this.configs = data.config
          this.properties = data.properties
          this.logs = data.logs
          this.assets_numbers = data.assets_numbers
          this.setTitle(this.configs.name)
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.serversLoading = false
        })
    }
  }
}
</script>

<style lang="scss">
.server-settings {
  .hr-assets {
    background-color: #c3c3c3;
  }
  .assets_numbers {
    .button {
      margin-right: 1rem;
      margin-bottom: 1rem;
      &.models {
        border-color: #fec5c7;
        background-color: #fec5c7;
        margin-right: 5rem;
        @media screen and (max-width: 1023px) {
          margin-right: 1rem;
        }
      }
      &.sources {
        border-color: #fcecb3;
        background-color: #fcecb3;
      }
      &.pipelines {
        border-color: #c6e3ff;
        background-color: #c6e3ff;
      }
      &.outputs {
        border-color: #e4b9f7;
        background-color: #e4b9f7;
      }
    }
  }
  .merge-layout {
    .columns {
      .column {
        .status-indicator {
          text-align: center;
          display: block;
        }
        .buttons {
          margin-bottom: 1rem;
          margin-top: 1rem;
          width: max-content;
          margin-left: auto;
          margin-right: auto;
          .button {
            font-size: 1.5rem;
            &.play {
              border: 0;
              padding: 0;
              margin-left: 0.5rem;
              height: max-content;
              color: #4bbd4d;
              background-color: #fff;
            }
            &.stop {
              border: 0;
              padding: 0;
              margin-left: 0.5rem;
              height: max-content;
              color: #fc4850;
              background-color: #fff;
            }
            &.reload {
              border: 0;
              padding: 0;
              margin-left: 0.5rem;
              height: max-content;
            }
          }
        }
        .logs-link {
          text-align: center;
          width: 100%;
          display: block;
          i {
            margin-left: 0.5rem;
          }
        }
      }
    }
  }
}
</style>
